// src/Hooks/useKmlParser.js

import JSZip from 'jszip';

const kmlColorToCSS = (kmlColor) => {
  if (!kmlColor || kmlColor.length !== 8) return null;
  const b = kmlColor.substring(2, 4);
  const g = kmlColor.substring(4, 6);
  const r = kmlColor.substring(6, 8);
  return `#${r}${g}${b}`;
};

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const useKmlParser = (stateStore) => {
  const { refs, setters } = stateStore;

  const parseKML = (kmlText) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(kmlText, 'text/xml');

    if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
      setters.setAlertState({
        open: true,
        message: 'Failed to parse file. Invalid XML format.',
        severity: 'error',
      });
      return;
    }

    const styleColors = {};
    const styleRefs = {};

    const styles = xmlDoc.getElementsByTagName('Style');
    for (let i = 0; i < styles.length; i++) {
      let id = styles[i].getAttribute('id');

      if (
        !id &&
        styles[i].parentNode &&
        styles[i].parentNode.tagName.includes('CascadingStyle')
      ) {
        id =
          styles[i].parentNode.getAttribute('kml:id') ||
          styles[i].parentNode.getAttribute('id');
      }

      if (id) {
        const iconStyle = styles[i].getElementsByTagName('IconStyle')[0];
        if (iconStyle) {
          const colorNode = iconStyle.getElementsByTagName('color')[0];
          if (colorNode) {
            styleColors[id] = kmlColorToCSS(colorNode.textContent.trim());
          } else {
            const icon = iconStyle.getElementsByTagName('Icon')[0];
            if (icon) {
              const href = icon.getElementsByTagName('href')[0];
              if (href) {
                const match = href.textContent.match(/color=([0-9a-fA-F]{6})/i);
                if (match) styleColors[id] = `#${match[1]}`;
              }
            }
          }
        }
      }
    }

    const styleMaps = xmlDoc.getElementsByTagName('StyleMap');
    for (let i = 0; i < styleMaps.length; i++) {
      const id = styleMaps[i].getAttribute('id');
      if (id) {
        const pairs = styleMaps[i].getElementsByTagName('Pair');
        for (let j = 0; j < pairs.length; j++) {
          const key = pairs[j].getElementsByTagName('key')[0];
          if (key && key.textContent === 'normal') {
            const styleUrlNode = pairs[j].getElementsByTagName('styleUrl')[0];
            if (styleUrlNode) {
              let sUrl = styleUrlNode.textContent.trim();
              if (sUrl.startsWith('#')) sUrl = sUrl.substring(1);
              styleRefs[id] = sUrl;
            }
          }
        }
      }
    }

    const placemarks = xmlDoc.getElementsByTagName('Placemark');
    const extractedData = [];
    let foundRestrictedImages = false;

    for (let i = 0; i < placemarks.length; i++) {
      const placemark = placemarks[i];
      const nameNode = placemark.getElementsByTagName('name')[0];
      const name = nameNode ? nameNode.textContent : 'Unnamed';

      const descNode = placemark.getElementsByTagName('description')[0];
      const description = descNode ? descNode.textContent : '';

      const styleUrlNode = placemark.getElementsByTagName('styleUrl')[0];
      const styleUrl = styleUrlNode ? styleUrlNode.textContent : '';

      let lat = null;
      let lng = null;
      const pointNode = placemark.getElementsByTagName('Point')[0];
      if (pointNode) {
        const coordNode = pointNode.getElementsByTagName('coordinates')[0];
        if (coordNode) {
          const coords = coordNode.textContent.trim().split(',');
          if (coords.length >= 2) {
            lng = parseFloat(coords[0]);
            lat = parseFloat(coords[1]);
          }
        }
      }

      let color = null;
      const inlineStyle = placemark.getElementsByTagName('Style')[0];
      if (inlineStyle) {
        const inlineIconStyle = inlineStyle.getElementsByTagName('IconStyle')[0];
        if (inlineIconStyle) {
          const cNode = inlineIconStyle.getElementsByTagName('color')[0];
          if (cNode) color = kmlColorToCSS(cNode.textContent.trim());
        }
      }

      if (!color && styleUrl) {
        let currentRef = styleUrl;
        if (currentRef.startsWith('#')) currentRef = currentRef.substring(1);

        for (let depth = 0; depth < 5; depth++) {
          if (styleColors[currentRef]) {
            color = styleColors[currentRef];
            break;
          } else if (styleRefs[currentRef]) {
            currentRef = styleRefs[currentRef];
          } else {
            break;
          }
        }
      }

      let imageCount = 0;
      let imageUrls = [];

      const carouselNode = placemark.getElementsByTagName('gx:Carousel')[0];
      if (carouselNode) {
        const images = carouselNode.getElementsByTagName('gx:Image');
        for (let j = 0; j < images.length; j++) {
          const src = images[j].textContent.trim();
          if (src.includes('google.com/hostedimage')) {
            foundRestrictedImages = true;
          }
          imageUrls.push(src);
          imageCount++;
        }
      }

      if (description) {
        const imgMatches = description.match(/<img[^>]+>/gi);
        if (imgMatches) {
          for (const imgTag of imgMatches) {
            imageCount++;
            const srcMatch = imgTag.match(/src=["']([^"']+)["']/i);
            if (srcMatch && srcMatch[1]) {
              const src = srcMatch[1];
              if (src.includes('google.com/hostedimage')) {
                foundRestrictedImages = true;
              }
              imageUrls.push(src);
            }
          }
        }
      }

      const hasRichText = /<[a-z][\s\S]*>/i.test(description);
      const cleanDescription = stripHtml(description);

      if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
        extractedData.push({
          id: `kml_${i}`,
          name,
          description,
          cleanDescription,
          styleUrl,
          lat,
          lng,
          color,
          imageCount,
          imageUrls,
          hasRichText,
        });
      }
    }

    extractedData.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setters.setParsedData(extractedData);
    // AMENDED: Initialize with an empty selection array instead of all IDs
    setters.setSelectedImportIds([]);

    if (foundRestrictedImages) {
      setters.setRestrictedAlert({ open: true });
    }
  };

  const handleImportClick = () => {
    if (refs.fileInputRef.current) refs.fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setters.setOriginalFilename(file.name);
    setters.setIsProcessing(true);

    try {
      if (file.name.toLowerCase().endsWith('.kmz')) {
        const zip = new JSZip();
        const loadedZip = await zip.loadAsync(file);

        const kmlFilename = Object.keys(loadedZip.files).find((name) =>
          name.toLowerCase().endsWith('.kml'),
        );

        if (!kmlFilename) {
          throw new Error('No .kml file found inside the .kmz archive.');
        }

        const kmlText = await loadedZip.file(kmlFilename).async('string');
        parseKML(kmlText);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          parseKML(e.target.result);
        };
        reader.readAsText(file);
      }

      setters.setViewMode('DATA_VIEW');
    } catch (error) {
      console.error('Error processing file:', error);
      setters.setAlertState({
        open: true,
        message: `Error reading file: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
      if (refs.fileInputRef.current) refs.fileInputRef.current.value = '';
    }
  };

  return { handleImportClick, handleFileChange };
};
